import axios from "axios";

// Resolve the backend URL dynamically.
// - In dev/preview: use REACT_APP_BACKEND_URL (which points to the preview backend).
// - When deployed to ANY domain (custom subdomain, emergent.host URL, etc.):
//   use the page's own origin (frontend + backend are served from the same domain).
// This makes the build portable across environments without rebuilding.
function resolveBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
  if (typeof window === "undefined") return envUrl;
  try {
    const envHost = envUrl ? new URL(envUrl).hostname : "";
    const currentHost = window.location.hostname;
    if (envHost && currentHost && currentHost !== envHost) {
      // Frontend is loaded from a different domain than the configured backend
      // (e.g. deployed to books.toddsnursery.com while env points at preview).
      // Same-origin is the correct backend in this case.
      return window.location.origin;
    }
  } catch (e) {
    // Malformed env URL — fall back to same-origin
    return window.location.origin;
  }
  return envUrl;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Auth is delivered via the backend's httpOnly cookie (set on /auth/login).
// `withCredentials: true` ensures every request sends that cookie.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return JSON.stringify(e);
        const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
        // Pydantic puts the failing field path in `loc` as ["body", "field_name"]
        // (or for nested: ["body", "items", 0, "field_name"]). Strip the wrapper
        // and surface a readable field name so users see WHICH field failed,
        // not just "Field required" with no context.
        if (Array.isArray(e.loc) && e.loc.length > 0) {
          const path = e.loc
            .filter((p) => p !== "body" && p !== "query" && p !== "path")
            .map((p) => (typeof p === "number" ? `[${p}]` : String(p)))
            .join(".");
          if (path) return `${path}: ${msg}`;
        }
        // Empty path + generic "Field required" is a useless toast. Add a hint so
        // the user knows they're hitting a validation issue (not a server crash)
        // and can dig in via DevTools/Network.
        if (msg === "Field required" || msg === "field required") {
          return "Request is missing required information. Please refresh the page and try again — if it persists, check the browser console for the failing field.";
        }
        return msg;
      })
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Normalize FastAPI/Pydantic 422 error bodies before they reach call sites.
// Without this, `error.response.data.detail` may be an array of validation-error
// objects ({type, loc, msg, input, url}). Many components do
// `toast.error(e.response.data.detail || "...")`, and rendering that array as a
// React child triggers React error #31 ("Objects are not valid as a React
// child"). The interceptor converts `detail` to a friendly string in place so
// every existing call site stays safe.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const data = error?.response?.data;
      if (data && data.detail !== undefined && typeof data.detail !== "string") {
        data.detail = formatApiErrorDetail(data.detail);
      }
    } catch (_e) {
      /* defensive: never let normalization itself raise */
    }
    return Promise.reject(error);
  }
);
